/**
 * PmsCalendar — Calendario Maestro (Agosto 2028 demo).
 *
 * Desktop/tablet: 10 villas × 31 days grid grouped by pool. Reservation and
 * block bars colored by occupancy type (guest, owner, maintenance).
 *
 * Mobile: today + 7-day cards.
 *
 * Click a bar → jumps to `/pms/reservations/:id` for reservations; blocks
 * show a small info tooltip via `title` attr (no modal — checkpoint A).
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Section } from "@/pages/Shell";
import PmsShell from "@/pages/admin/pms/PmsShell";
import { House, Bed, Wrench, ArrowRight } from "@phosphor-icons/react";
import {
  VILLAS, POOLS, DEMO_MONTH, DEMO_MONTH_LABEL, DEMO_TODAY_ISO,
  BOOKING_STATUS_LABEL, STAY_STATUS_LABEL,
} from "@/data/pmsDemoData";
import {
  monthRange, datesBetweenExclusive, addDays, reservationsForVilla,
  blocksForVilla, getChannel, getVilla, getVillaStateOn, effectiveBlockEnd,
  formatEUR,
} from "@/lib/pmsSelectors";
import { usePmsBlocks, usePmsReservations } from "@/lib/pmsRuntimeStore";

const BAR_STYLE = {
  guest_confirmed:  "bg-[#9C5236] text-white",
  guest_in_stay:    "bg-[#9C5236] text-white ring-2 ring-amber-300 dark:ring-amber-500",
  guest_pending:    "bg-amber-500 text-white",
  guest_cancelled:  "bg-stone-200 dark:bg-stone-800 text-stone-500 line-through",
  owner_stay:       "bg-stone-800 dark:bg-stone-300 text-white dark:text-stone-900",
  maintenance:      "bg-red-400 dark:bg-red-500 text-white",
  admin_block:      "bg-stone-500 text-white",
};

const CHANNEL_ICON_LABEL = {
  booking: "B",
  airbnb: "A",
  direct_web: "D",
  manual: "M",
  whatsapp: "W",
  walk_in: "W",
};

export default function PmsCalendar() {
  // Suscripción al runtime store: los blocks generados desde Housekeeping y
  // las reservas creadas desde el alta de Reservas re-renderizan el
  // calendario sin refresh.
  usePmsBlocks();
  usePmsReservations();

  const { firstISO, lastISO, days } = useMemo(() => monthRange(DEMO_MONTH), []);
  const dayList = useMemo(
    () => datesBetweenExclusive(firstISO, addDays(lastISO, 1)),
    [firstISO, lastISO],
  );
  const [poolFilter, setPoolFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [tip, setTip] = useState(null); // { x, y, content }

  const showTip = (evt, content) => {
    setTip({ x: evt.clientX, y: evt.clientY, content });
  };
  const hideTip = () => setTip(null);

  const filteredVillas = useMemo(
    () => (poolFilter === "all" ? VILLAS : VILLAS.filter((v) => v.pool_id === poolFilter)),
    [poolFilter],
  );

  const matchesSearch = (r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      r.reservation_id.toLowerCase().includes(q)
      || (r.guest?.name || "").toLowerCase().includes(q)
      || (r.assigned_villa_id || "").toLowerCase().includes(q)
    );
  };

  return (
    <PmsShell>
      <Section
        title={`Calendario · ${DEMO_MONTH_LABEL}`}
        subtitle="10 villas agrupadas por pool. Barras coloreadas por tipo de ocupación."
      >
        <div className="mb-4 flex flex-wrap gap-2 items-center" data-testid="pms-calendar-controls">
          <div className="inline-flex bg-stone-100 dark:bg-stone-800 rounded-full p-0.5">
            {[
              { key: "all", label: "Todos" },
              { key: "vistas", label: "Vistas" },
              { key: "prive", label: "Privè" },
            ].map((opt) => {
              const active = poolFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  onClick={() => setPoolFilter(opt.key)}
                  data-testid={`pms-calendar-pool-${opt.key}`}
                  className={`text-xs px-3 py-1 rounded-full ${
                    active
                      ? "bg-white dark:bg-stone-950 text-stone-900 dark:text-stone-100 shadow-sm"
                      : "text-stone-500 dark:text-stone-400"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <input
            type="text"
            placeholder="Buscar reserva o huésped…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-xs px-3 py-1.5 rounded border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-950 text-stone-800 dark:text-stone-200 min-w-[200px]"
            data-testid="pms-calendar-search"
          />
          <Legend />
        </div>

        {/* Desktop / tablet grid */}
        <div className="hidden md:block dv-card overflow-x-auto p-0" data-testid="pms-calendar-grid">
          <table className="w-full text-[10px] border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white dark:bg-stone-950 z-10 text-left px-2 py-2 text-stone-500 dark:text-stone-400 border-b border-r border-stone-200 dark:border-stone-800 min-w-[130px]">
                  Villa
                </th>
                {dayList.map((iso) => {
                  const d = new Date(iso + "T00:00:00Z");
                  const isToday = iso === DEMO_TODAY_ISO;
                  const dow = d.getUTCDay();
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <th
                      key={iso}
                      className={`px-0 py-1 text-center border-b border-stone-200 dark:border-stone-800 font-normal tabular-nums ${
                        isToday
                          ? "bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 font-semibold"
                          : isWeekend
                          ? "bg-stone-50 dark:bg-stone-900 text-stone-400"
                          : "text-stone-500 dark:text-stone-400"
                      }`}
                      style={{ minWidth: 22, width: 22 }}
                      title={iso}
                    >
                      {d.getUTCDate()}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {POOLS.map((pool) => {
                const poolVillas = filteredVillas.filter((v) => v.pool_id === pool.pool_id);
                if (poolVillas.length === 0) return null;
                return (
                  <React.Fragment key={pool.pool_id}>
                    <tr>
                      <td
                        colSpan={days + 1}
                        className="px-2 py-1 text-[10px] uppercase tracking-widest bg-stone-100 dark:bg-stone-900 text-stone-500 dark:text-stone-400"
                      >
                        {pool.label} · {poolVillas.length} villas
                      </td>
                    </tr>
                    {poolVillas.map((v) => (
                      <CalendarRow
                        key={v.villa_id}
                        villa={v}
                        dayList={dayList}
                        matchesSearch={matchesSearch}
                        showTip={showTip}
                        hideTip={hideTip}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile: today + 7 days */}
        <div className="md:hidden space-y-3" data-testid="pms-calendar-mobile">
          <MobileTodayCards />
        </div>
      </Section>

      {/* Hover tooltip (desktop). Renderizado fuera de la table para
          escapar el overflow-x-auto del contenedor scrollable. */}
      <CalendarTooltip tip={tip} />
    </PmsShell>
  );
}

// ---------------------------------------------------------------------------
// Row: one villa × days
// ---------------------------------------------------------------------------

function CalendarRow({ villa, dayList, matchesSearch, showTip, hideTip }) {
  const reservations = reservationsForVilla(villa.villa_id).filter(matchesSearch);
  const blocks = blocksForVilla(villa.villa_id);

  // Map day → JSX content (bar segment or empty)
  const cellNodes = {};

  // Blocks first (background). Usa `effectiveBlockEnd` para que un block
  // cuya incidencia se ha resuelto no siga bloqueando fechas posteriores.
  for (const b of blocks) {
    const start = b.start_date;
    const end = effectiveBlockEnd(b);
    if (!end || end < start) continue;
    const nightsList = datesBetweenExclusive(start, addDays(end, 1))
      .filter((d) => d >= dayList[0] && d <= dayList[dayList.length - 1]);
    if (nightsList.length === 0) continue;
    const type = b.type === "OWNER_STAY" ? "owner_stay"
      : b.type === "MAINTENANCE" ? "maintenance"
      : "admin_block";
    const label = b.type === "OWNER_STAY" ? "Owner"
      : b.type === "MAINTENANCE" ? "Maint" : "Admin";
    for (let i = 0; i < nightsList.length; i++) {
      const iso = nightsList[i];
      cellNodes[iso] = { kind: "block", type, label: i === 0 ? label : "", nights: nightsList, block: b };
    }
  }

  // Reservations (foreground)
  for (const r of reservations) {
    const nightsList = datesBetweenExclusive(r.check_in, r.check_out)
      .filter((d) => d >= dayList[0] && d <= dayList[dayList.length - 1]);
    if (nightsList.length === 0) continue;
    const type = r.booking_status === "cancelled"
      ? "guest_cancelled"
      : r.stay_status === "checked_in"
      ? "guest_in_stay"
      : r.booking_status === "pending"
      ? "guest_pending"
      : "guest_confirmed";
    for (let i = 0; i < nightsList.length; i++) {
      const iso = nightsList[i];
      cellNodes[iso] = { kind: "res", type, r, isFirst: i === 0, isLast: i === nightsList.length - 1 };
    }
  }

  return (
    <tr>
      <td className="sticky left-0 bg-white dark:bg-stone-950 z-10 px-2 py-1 border-r border-stone-200 dark:border-stone-800 text-stone-800 dark:text-stone-200 whitespace-nowrap">
        <span className="text-[10px] text-stone-400 mr-1">{villa.villa_id}</span>
        {villa.name.replace("Villa ", "")}
      </td>
      {dayList.map((iso) => {
        const node = cellNodes[iso];
        const isToday = iso === DEMO_TODAY_ISO;
        const cellStyle = isToday ? "bg-amber-50 dark:bg-amber-950/20" : "";
        if (!node) {
          return (
            <td key={iso} className={`border-b border-stone-100 dark:border-stone-900 ${cellStyle}`} style={{ minWidth: 22, width: 22, height: 26 }} />
          );
        }
        if (node.kind === "block") {
          return (
            <td
              key={iso}
              className={`border-b border-stone-100 dark:border-stone-900 p-0 ${cellStyle}`}
              style={{ minWidth: 22, width: 22, height: 26 }}
              title={buildBlockTitle(node.block)}
            >
              <div
                className={`w-full h-[20px] mx-auto ${BAR_STYLE[node.type]} flex items-center justify-center text-[9px] cursor-help`}
                onMouseEnter={(e) => showTip && showTip(e, renderBlockTip(node.block))}
                onMouseLeave={() => hideTip && hideTip()}
              >
                {node.label}
              </div>
            </td>
          );
        }
        // reservation cell
        const r = node.r;
        const chLabel = CHANNEL_ICON_LABEL[r.channel] || "";
        return (
          <td
            key={iso}
            className={`border-b border-stone-100 dark:border-stone-900 p-0 ${cellStyle}`}
            style={{ minWidth: 22, width: 22, height: 26 }}
            title={`${r.reservation_id} · ${getChannel(r.channel).label} · ${r.check_in} → ${r.check_out}`}
          >
            <Link
              to={`/pms/reservations/${r.reservation_id}`}
              className={`block w-full h-[20px] ${BAR_STYLE[node.type]} flex items-center justify-center text-[9px] hover:opacity-80 cursor-pointer`}
              onMouseEnter={(e) => showTip && showTip(e, renderReservationTip(r))}
              onMouseLeave={() => hideTip && hideTip()}
            >
              {node.isFirst ? chLabel : ""}
            </Link>
          </td>
        );
      })}
    </tr>
  );
}

function buildBlockTitle(b) {
  const parts = [
    `${b.type} · ${b.start_date} → ${b.end_date}`,
    `Motivo: ${b.reason || "—"}`,
    `Fairness compensable: ${b.fairness_compensable ? "Sí" : "No"}`,
  ];
  if (b.issue_id) parts.push(`Incidencia: ${b.issue_id}`);
  if (b.resolved_at) parts.push(`Resuelto: ${String(b.resolved_at).slice(0, 16).replace("T", " ")}`);
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Tooltip visual (hover desktop)
// ---------------------------------------------------------------------------

const TIP_WIDTH = 260;
const TIP_HEIGHT_EST = 180;
const TIP_OFFSET = 14;

function CalendarTooltip({ tip }) {
  if (!tip) return null;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  let left = tip.x + TIP_OFFSET;
  let top = tip.y + TIP_OFFSET;
  if (left + TIP_WIDTH > vw - 8) left = Math.max(8, tip.x - TIP_OFFSET - TIP_WIDTH);
  if (top + TIP_HEIGHT_EST > vh - 8) top = Math.max(8, tip.y - TIP_OFFSET - TIP_HEIGHT_EST);
  return (
    <div
      role="tooltip"
      data-testid="pms-calendar-tooltip"
      style={{
        position: "fixed",
        top,
        left,
        width: TIP_WIDTH,
        zIndex: 60,
        pointerEvents: "none",
      }}
      className="rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 shadow-lg p-3 text-xs text-stone-700 dark:text-stone-200"
    >
      {tip.content}
    </div>
  );
}

function TipRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 leading-relaxed">
      <span className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">{label}</span>
      <span className="text-right text-stone-800 dark:text-stone-100 tabular-nums">{value}</span>
    </div>
  );
}

function TipHeader({ kicker, title, accent }) {
  return (
    <div className="mb-1.5">
      <div className={`text-[10px] uppercase tracking-widest ${accent || "text-stone-400 dark:text-stone-500"}`}>
        {kicker}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-stone-900 dark:text-stone-100">
        {title}
      </div>
    </div>
  );
}

function renderReservationTip(r) {
  const villa = getVilla(r.assigned_villa_id);
  const channel = getChannel(r.channel);
  const isCancelled = r.booking_status === "cancelled";
  const isHold = r.booking_status === "hold";
  const isPending = r.booking_status === "pending";
  const accent = isCancelled ? "text-stone-500"
    : isHold ? "text-amber-700 dark:text-amber-300"
    : isPending ? "text-amber-700 dark:text-amber-300"
    : "text-[#9C5236] dark:text-amber-300";
  const kicker = isHold ? `Hold · ${channel.label}` : `Reserva · ${channel.label}`;
  return (
    <>
      <TipHeader kicker={kicker} title={r.reservation_id} accent={accent} />
      <div className="space-y-0.5">
        <TipRow label="Villa"    value={`${villa?.villa_id} · ${villa?.name.replace("Villa ", "")}`} />
        <TipRow label="Fechas"   value={`${r.check_in} → ${r.check_out}`} />
        <TipRow label="Noches"   value={String(r.nights)} />
        <TipRow label="Ingresos" value={formatEUR(r.accommodation_revenue_eur)} />
        <TipRow label="Booking"  value={BOOKING_STATUS_LABEL[r.booking_status] || r.booking_status} />
        <TipRow label="Estancia" value={STAY_STATUS_LABEL[r.stay_status]     || r.stay_status} />
      </div>
      <div className="mt-2 text-[10px] text-stone-400 dark:text-stone-500">
        Datos huésped ocultos en el calendario · Ver ficha para detalle.
      </div>
    </>
  );
}

function renderBlockTip(b) {
  const villa = getVilla(b.villa_id);
  const isMaint = b.type === "MAINTENANCE";
  const isOwner = b.type === "OWNER_STAY";
  const isAdmin = b.type === "ADMIN_BLOCK";
  const typeLabel = isMaint ? "Maintenance"
    : isOwner ? "Uso propietario"
    : isAdmin ? "Bloqueo administrativo"
    : b.type;
  const accent = isMaint ? "text-red-600 dark:text-red-400"
    : isOwner ? "text-stone-500 dark:text-stone-400"
    : "text-stone-500 dark:text-stone-400";
  const title = b.reason || (isOwner ? "Owner stay" : isMaint ? "Mantenimiento" : "Bloqueo");
  return (
    <>
      <TipHeader kicker={typeLabel} title={title} accent={accent} />
      <div className="space-y-0.5">
        <TipRow label="Villa"    value={`${villa?.villa_id} · ${villa?.name.replace("Villa ", "")}`} />
        <TipRow label="Fechas"   value={`${b.start_date} → ${b.end_date}`} />
        <TipRow label="Noches"   value={String(b.nights)} />
        {isMaint && (
          <TipRow
            label="Fairness comp."
            value={b.fairness_compensable ? "Sí" : "No"}
          />
        )}
        {b.issue_id && (
          <TipRow label="Incidencia" value={b.issue_id} />
        )}
        {b.status && (
          <TipRow label="Estado" value={b.status === "resolved" ? "Resuelto" : b.status} />
        )}
      </div>
      {b.resolved_at && (
        <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
          Resuelto · {String(b.resolved_at).slice(0, 16).replace("T", " ")}
        </div>
      )}
      {b.origin === "created_in_session" && !b.resolved_at && (
        <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
          Creado en sesión
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------

function Legend() {
  const items = [
    { color: "bg-[#9C5236]",         label: "Reserva" },
    { color: "bg-amber-500",          label: "Pendiente" },
    { color: "bg-stone-800 dark:bg-stone-300", label: "Owner" },
    { color: "bg-red-400 dark:bg-red-500", label: "Maintenance" },
    { color: "bg-stone-200 dark:bg-stone-800", label: "Cancelada" },
  ];
  return (
    <div className="ml-auto flex flex-wrap gap-2 text-[10px] text-stone-500 dark:text-stone-400">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1">
          <span className={`w-3 h-2 rounded ${it.color}`} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile view: today + next 7 days per villa
// ---------------------------------------------------------------------------

function MobileTodayCards() {
  const nextWeek = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) out.push(addDays(DEMO_TODAY_ISO, i));
    return out;
  }, []);

  return (
    <>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
        Hoy y próximos 7 días
      </div>
      {VILLAS.map((v) => {
        const rows = nextWeek.map((iso) => ({ iso, state: getVillaStateOn(v.villa_id, iso) }));
        const activeCount = rows.filter((row) => row.state.state !== "AVAILABLE").length;
        return (
          <div key={v.villa_id} className="dv-card" data-testid={`pms-calendar-mobile-${v.villa_id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-stone-900 dark:text-stone-100">
                <span className="text-[10px] text-stone-400 mr-1">{v.villa_id}</span>
                {v.name}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
                {activeCount}/7 con actividad
              </div>
            </div>
            <ul className="text-xs space-y-1">
              {rows.map((row) => (
                <MobileDayRow key={row.iso} iso={row.iso} villa={v} state={row.state} />
              ))}
            </ul>
          </div>
        );
      })}
    </>
  );
}

function MobileDayRow({ iso, villa, state }) {
  const isToday = iso === DEMO_TODAY_ISO;
  const d = new Date(iso + "T00:00:00Z");
  const label = d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
  let text = "Disponible";
  let icon = null;
  let link = null;
  if (state.state === "ARRIVAL_TODAY") {
    text = `Llega ${state.reservation.guest?.name || "—"}`;
    icon = <Bed size={12} />;
    link = `/pms/reservations/${state.reservation.reservation_id}`;
  } else if (state.state === "OCCUPIED") {
    text = `In-house · ${state.reservation.guest?.name || "—"}`;
    icon = <House size={12} />;
    link = `/pms/reservations/${state.reservation.reservation_id}`;
  } else if (state.state === "CLEANING") {
    text = "Por limpiar";
  } else if (state.state === "OWNER_STAY") {
    text = "Owner stay";
  } else if (state.state === "MAINTENANCE") {
    text = `Mantenimiento · ${state.block?.reason || ""}`;
    icon = <Wrench size={12} />;
  }
  const inner = (
    <div className={`flex items-center gap-2 ${isToday ? "font-medium text-stone-900 dark:text-stone-100" : "text-stone-600 dark:text-stone-400"}`}>
      <span className="w-16 shrink-0 tabular-nums text-[10px] uppercase">{label}</span>
      {icon}
      <span className="truncate">{text}</span>
      {link && <ArrowRight size={10} className="ml-auto text-stone-400" />}
    </div>
  );
  return (
    <li>
      {link ? <Link to={link} className="hover:underline">{inner}</Link> : inner}
    </li>
  );
}

